import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Filter, ExternalLink, Copy } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

const STATUS = [
  { value: "unpaid",   label: "Unpaid",    tone: "border-amber-300 bg-amber-50 text-amber-700" },
  { value: "paid",     label: "Paid",      tone: "border-emerald-300 bg-emerald-50 text-emerald-700" },
  { value: "partial",  label: "Partial",   tone: "border-blue-300 bg-blue-50 text-blue-700" },
  { value: "refunded", label: "Refunded",  tone: "border-zinc-300 bg-zinc-100 text-zinc-700" },
  { value: "void",     label: "Void",      tone: "border-zinc-300 bg-zinc-100 text-zinc-500" },
];
const KINDS = [
  { value: "general",     label: "General" },
  { value: "event",       label: "Event Booking" },
  { value: "bulk",        label: "Bulk Order" },
  { value: "franchise",   label: "Franchise" },
  { value: "custom_pop",  label: "Custom Popsicle" },
  { value: "quotation",   label: "Quotation" },
];
const statusMeta = (k) => STATUS.find((s) => s.value === k) || STATUS[0];
const kindLabel = (k) => (KINDS.find((x) => x.value === k) || {}).label || k;
const fmtMoney = (amt, cur = "INR") => new Intl.NumberFormat("en-IN", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(amt || 0);

const BLANK_ITEM = () => ({ description: "", quantity: 1, unit_price: 0, amount: 0 });
const BLANK = () => ({
  customer_name: "", customer_phone: "", customer_email: "",
  invoice_kind: "general", notes: "",
  items: [BLANK_ITEM()],
  subtotal: 0, tax: 0, total_amount: 0, currency: "INR", due_date: "",
});

export default function AdminInvoices() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    const q = new URLSearchParams();
    if (statusFilter !== "all") q.set("payment_status", statusFilter);
    if (kindFilter !== "all") q.set("kind", kindFilter);
    api.get(`/admin/invoices${q.toString() ? `?${q}` : ""}`).then((r) => setItems(r.data || []));
  }, [statusFilter, kindFilter]);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { all: items.length, paid: 0, unpaid: 0, total_billed: 0, total_collected: 0 };
    items.forEach((i) => {
      c.total_billed += i.total_amount || 0;
      if (i.payment_status === "paid") { c.paid += 1; c.total_collected += i.amount_paid || i.total_amount; }
      else if (i.payment_status === "unpaid") c.unpaid += 1;
    });
    return c;
  }, [items]);

  const recalc = (inv) => {
    const items = (inv.items || []).map((it) => ({ ...it, amount: Number(it.quantity || 0) * Number(it.unit_price || 0) }));
    const subtotal = items.reduce((a, b) => a + b.amount, 0);
    const tax = Number(inv.tax || 0);
    return { ...inv, items, subtotal, total_amount: subtotal + tax };
  };

  const startNew = () => { setEditing(recalc(BLANK())); setOpen(true); };
  const startEdit = (inv) => { setEditing({ ...inv, items: inv.items || [BLANK_ITEM()] }); setOpen(true); };

  const save = async () => {
    const body = recalc(editing);
    delete body.id; delete body.invoice_number; delete body.created_at; delete body.payment_status;
    delete body.amount_paid; delete body.paid_at; delete body.stripe_session_id; delete body.admin_notes; delete body.updated_at;
    try {
      if (editing.id) await api.put(`/admin/invoices/${editing.id}`, body);
      else await api.post("/admin/invoices", body);
      toast.success("Invoice saved");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (iid, payment_status) => {
    try { await api.patch(`/admin/invoices/${iid}/status`, { payment_status }); toast.success("Status updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (iid) => {
    if (!window.confirm("Delete this invoice?")) return;
    await api.delete(`/admin/invoices/${iid}`); toast.success("Deleted"); load();
  };

  const copyLink = (inv_no) => {
    const url = `${window.location.origin}/pay?invoice=${inv_no}`;
    navigator.clipboard.writeText(url);
    toast.success("Payment link copied");
  };

  return (
    <div data-testid="admin-invoices" className="space-y-8">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Invoices &amp; Billing</div>
          <h1 className="editorial-h1 text-5xl mt-2">{items.length} invoices.</h1>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55"><Filter size={12} /></div>
          <select className="pp-input bg-transparent" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="ai-filter-status" style={{ width: "auto" }}>
            <option value="all">All statuses</option>
            {STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select className="pp-input bg-transparent" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} data-testid="ai-filter-kind" style={{ width: "auto" }}>
            <option value="all">All types</option>
            {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
          <button onClick={startNew} data-testid="ai-new" className="btn-pill btn-dark text-sm"><Plus size={16} /> New invoice</button>
        </div>
      </header>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="card-elevated p-5"><div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Total billed</div><div className="font-display text-3xl mt-1">{fmtMoney(counts.total_billed)}</div></div>
        <div className="card-elevated p-5"><div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Collected</div><div className="font-display text-3xl mt-1 text-emerald-600">{fmtMoney(counts.total_collected)}</div></div>
        <div className="card-elevated p-5"><div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Outstanding</div><div className="font-display text-3xl mt-1 text-amber-600">{fmtMoney(counts.total_billed - counts.total_collected)}</div></div>
        <div className="card-elevated p-5"><div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Paid · Unpaid</div><div className="font-display text-3xl mt-1">{counts.paid} <span className="text-brand-charcoal/40">/</span> {counts.unpaid}</div></div>
      </div>

      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">
            <tr className="border-b border-brand-line">
              <th className="p-4">Invoice #</th><th className="p-4">Customer</th><th className="p-4">Type</th><th className="p-4 text-right">Amount</th><th className="p-4">Status</th><th className="p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((inv) => {
              const sm = statusMeta(inv.payment_status);
              return (
                <tr key={inv.id} data-testid={`ai-row-${inv.id}`} className="border-b border-brand-line last:border-0 hover:bg-brand-muted/40">
                  <td className="p-4 align-top">
                    <div className="font-display text-lg">{inv.invoice_number}</div>
                    <div className="text-xs text-brand-charcoal/55">{(inv.created_at || "").slice(0,10)}</div>
                  </td>
                  <td className="p-4 align-top">
                    <div className="font-medium">{inv.customer_name}</div>
                    <div className="text-xs text-brand-charcoal/55">{inv.customer_phone}</div>
                  </td>
                  <td className="p-4 align-top text-xs uppercase tracking-[0.18em] text-brand-red">{kindLabel(inv.invoice_kind)}</td>
                  <td className="p-4 align-top text-right font-display text-lg">{fmtMoney(inv.total_amount, inv.currency)}</td>
                  <td className="p-4 align-top">
                    <select value={inv.payment_status} onChange={(e) => setStatus(inv.id, e.target.value)} data-testid={`ai-status-${inv.id}`}
                      className={`text-[10px] uppercase tracking-[0.18em] border rounded-full px-2.5 py-1 ${sm.tone}`}>
                      {STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="p-4 align-top">
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => copyLink(inv.invoice_number)} title="Copy payment link" className="btn-pill btn-ghost text-xs border border-brand-line" data-testid={`ai-copy-${inv.id}`}><Copy size={12} /> Link</button>
                      <a href={`/pay?invoice=${inv.invoice_number}`} target="_blank" rel="noreferrer" className="btn-pill btn-ghost text-xs border border-brand-line"><ExternalLink size={12} /></a>
                      <button onClick={() => startEdit(inv)} data-testid={`ai-edit-${inv.id}`} className="btn-pill btn-ghost text-xs border border-brand-line">Edit</button>
                      <button onClick={() => del(inv.id)} className="btn-pill btn-ghost text-xs text-brand-red"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan="6" className="p-12 text-center text-brand-charcoal/60">No invoices match the current filter.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && editing && (
        <div className="fixed inset-0 z-50 bg-brand-charcoal/40 grid place-items-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">{editing.id ? `Editing ${editing.invoice_number}` : "New invoice"}</div>
                <div className="font-display text-3xl mt-1">Invoice details</div>
              </div>
              <button onClick={() => setOpen(false)}><X /></button>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Customer name</label><input data-testid="ai-f-name" className="pp-input" value={editing.customer_name} onChange={(e) => setEditing({ ...editing, customer_name: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Phone</label><input data-testid="ai-f-phone" className="pp-input" value={editing.customer_phone || ""} onChange={(e) => setEditing({ ...editing, customer_phone: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Email</label><input data-testid="ai-f-email" type="email" className="pp-input" value={editing.customer_email || ""} onChange={(e) => setEditing({ ...editing, customer_email: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Type</label>
                <select data-testid="ai-f-kind" className="pp-input bg-transparent" value={editing.invoice_kind} onChange={(e) => setEditing({ ...editing, invoice_kind: e.target.value })}>
                  {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
              </div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Due date</label><input type="date" className="pp-input" value={editing.due_date || ""} onChange={(e) => setEditing({ ...editing, due_date: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Currency</label><input className="pp-input" value={editing.currency || "INR"} onChange={(e) => setEditing({ ...editing, currency: e.target.value.toUpperCase() })} /></div>
            </div>

            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mb-2">Line items</div>
              <div className="space-y-2">
                {editing.items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <input className="pp-input col-span-6" placeholder="Description" value={it.description} onChange={(e) => { const items = [...editing.items]; items[idx] = { ...it, description: e.target.value }; setEditing(recalc({ ...editing, items })); }} />
                    <input type="number" min="0" step="0.01" className="pp-input col-span-2" placeholder="Qty" value={it.quantity} onChange={(e) => { const items = [...editing.items]; items[idx] = { ...it, quantity: Number(e.target.value) || 0 }; setEditing(recalc({ ...editing, items })); }} />
                    <input type="number" min="0" step="0.01" className="pp-input col-span-3" placeholder="Unit price" value={it.unit_price} onChange={(e) => { const items = [...editing.items]; items[idx] = { ...it, unit_price: Number(e.target.value) || 0 }; setEditing(recalc({ ...editing, items })); }} />
                    <button type="button" onClick={() => setEditing(recalc({ ...editing, items: editing.items.filter((_, i) => i !== idx) }))} className="col-span-1 text-brand-red"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => setEditing({ ...editing, items: [...editing.items, BLANK_ITEM()] })} className="btn-pill btn-ghost border border-brand-line text-xs mt-2"><Plus size={12} /> Add line</button>
            </div>

            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Tax</label><input type="number" min="0" step="0.01" className="pp-input" value={editing.tax || 0} onChange={(e) => setEditing(recalc({ ...editing, tax: Number(e.target.value) || 0 }))} /></div>
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Subtotal</div>
                <div className="font-display text-2xl">{fmtMoney(editing.subtotal || 0, editing.currency)}</div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mt-2">Total</div>
                <div className="font-display text-3xl text-brand-red">{fmtMoney(editing.total_amount || 0, editing.currency)}</div>
              </div>
            </div>

            <div className="mt-6"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Notes</label><textarea rows="2" className="pp-input" value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>

            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setOpen(false)} className="btn-pill btn-ghost text-sm">Cancel</button>
              <button onClick={save} data-testid="ai-f-save" className="btn-pill btn-dark text-sm">{editing.id ? "Save changes" : "Create invoice"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
